class FAQTabs extends HTMLElement {
	#activeTabIndex = -1;
	
	connectedCallback() {
		this.faqsTabItems = this.querySelectorAll('.faqs-tab__item');
		this.faqsTabSections = document.querySelectorAll('.shopify-section--faq');
		this.tablist = this.querySelector('[role="tablist"]');

		if (!this.faqsTabItems.length || !this.faqsTabSections.length || !this.tablist) {
			return;
		}

		if (this.faqsTabSections.length !== this.faqsTabItems.length) {
			console.error('FAQ sections and tab items must be the same length');
			return;
		}

		this.#setupTabs();
	}

	get activeTabIndex() {
		return this.#activeTabIndex;
	}

	set activeTabIndex(nextIndex) {
		const currentIndex = this.activeTabIndex;
		const maxIndex = this.faqsTabItems.length - 1;
		const minIndex = 0;

		if (nextIndex === currentIndex) return;

		if (nextIndex > maxIndex) {
			nextIndex = minIndex;
		} else if (nextIndex < minIndex) {
			nextIndex = maxIndex;
		}

		this.faqsTabItems[nextIndex].tabIndex = 0;
		this.faqsTabItems[nextIndex].setAttribute('aria-selected', 'true');
		this.faqsTabSections[nextIndex].hidden = false;
		
		const hasFocusedChild = document.activeElement.closest('faq-tabs') !== null;
		const isActiveTabAlreadyFocused = document.activeElement === this.faqsTabItems[nextIndex];

		if (hasFocusedChild && !isActiveTabAlreadyFocused) {
			this.faqsTabItems[nextIndex].focus();
		}

		if (currentIndex !== -1) {
			this.faqsTabItems[currentIndex].tabIndex = -1;
			this.faqsTabItems[currentIndex].setAttribute('aria-selected', 'false');
			this.faqsTabSections[currentIndex].hidden = true;
		}

		requestAnimationFrame(() => {
			this.faqsTabItems[nextIndex].scrollIntoView({ behavior: 'smooth', inline: 'center', block: 'nearest' });
		});

		this.#activeTabIndex = nextIndex;
	}

	#setupTabs() {
		const tabLength = this.faqsTabItems.length;

		for (let i = 0; i < tabLength; i++) {
			this.faqsTabSections[i].tabIndex = 0;
			this.faqsTabSections[i].setAttribute('role', 'tabpanel');
			this.faqsTabSections[i].setAttribute('aria-labelledby', this.faqsTabItems[i].id);
			this.faqsTabSections[i].hidden = true;

			this.faqsTabItems[i].setAttribute('aria-controls', this.faqsTabSections[i].id);
			this.faqsTabItems[i].addEventListener('click', () => this.activeTabIndex = i); 
		}

		this.activeTabIndex = 0;
		this.#handleHashChange();

		this.tablist.addEventListener('keyup', this.#handleKeyup.bind(this));
		window.addEventListener('hashchange', this.#handleHashChange.bind(this));
	}

	#handleKeyup(e) {
		if (e.key === 'ArrowRight') {
			e.preventDefault();
			e.stopPropagation();
			this.activeTabIndex++;
		} else if (e.key === 'ArrowLeft') {
			e.preventDefault();
			e.stopPropagation();
			this.activeTabIndex--;
		}
	}

	#handleHashChange() {
		const hash = window.location.hash.substring(1);

		if (!hash) return;

		const parts = hash.split('&');
		const tabPart = parts.find(part => part.startsWith('tab'));
		const questionPart = parts.find(part => part.startsWith('question'));

		if (!tabPart) return;
			
		const tabIndex = parseInt(tabPart.replace('tab', ''), 10) - 1;

		if (isNaN(tabIndex) || !this.faqsTabItems[tabIndex]) return;

		this.activeTabIndex = tabIndex;

		if (!questionPart) return;

		const questionIndex = parseInt(questionPart.replace('question', ''), 10);
		
		if (isNaN(questionIndex)) return;

		const activeTabSection = this.faqsTabSections[tabIndex];

		if (!activeTabSection) return;

		const questionItem = activeTabSection.querySelector(`details[data-question-index="${questionIndex}"]`);

		if (!questionItem) return;

		const accordionDisclosure = questionItem.closest('accordion-disclosure');
		
		if (!accordionDisclosure || typeof accordionDisclosure.open !== 'function') return;

		setTimeout(() => {
			accordionDisclosure.open();
			questionItem.scrollIntoView({ behavior: 'smooth', block: 'start' });
		}, 50);
	}
}

if (!customElements.get('faq-tabs')) {
	customElements.define('faq-tabs', FAQTabs);
}